import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { Portal, useProgress } from '@spazious/dollhouse-engine';

import { LoaderBar } from './LoaderBar.js';
import { LoaderContainer } from './LoaderContainer.js';
import { LoaderInner } from './LoaderInner.js';
import { LoaderLabel } from './LoaderLabel.js';

const defaultFormatter = (p: number) => `Loading ${p.toFixed(2)}%`;

export interface LoaderProps {
  /**
   * A function that returns a string to display as the loading label.
   */
  formatter?: (p: number) => string;
  /**
   * Whether the loader should be active by default.
   */
  defaultActive?: boolean;
}

/**
 * Renders a loading indicator. Uses the `useProgress` hook to track loading progress.
 */
export const Loader = forwardRef<HTMLDivElement, LoaderProps>(
  ({ formatter = defaultFormatter, defaultActive, ...restProps }, ref) => {
    const [labelProgress, setLabelProgress] = useState(formatter(0));
    const { active, progress } = useProgress();
    const progressRef = useRef(0);
    const rafRef = useRef(0);
    const [shown, setShown] = useState(defaultActive ?? active);

    useEffect(() => {
      let t: any;
      if (active !== shown) t = setTimeout(() => setShown(active), 300);
      return () => clearTimeout(t);
    }, [shown, active]);

    const updateProgress = useCallback(() => {
      progressRef.current += (progress - progressRef.current) / 2;
      if (progressRef.current > 0.95 * progress || progress === 100) progressRef.current = progress;
      setLabelProgress(formatter(progressRef.current));
      if (progressRef.current < progress) rafRef.current = requestAnimationFrame(updateProgress);
    }, [formatter, progress]);

    useEffect(() => {
      updateProgress();
      return () => cancelAnimationFrame(rafRef.current);
    }, [updateProgress]);

    return (
      <Portal enabled={shown}>
        <LoaderContainer ref={ref} active={active} {...restProps}>
          <LoaderInner>
            <LoaderBar progress={progress} />
            <LoaderLabel>{labelProgress}</LoaderLabel>
          </LoaderInner>
        </LoaderContainer>
      </Portal>
    );
  }
);
